/**
 * Shared airline vs hotel detection for transport modules (SBB, Swiss Travel Pass, Airport Taxi, …).
 * Persists router state because React Router drops `location.state` when navigate/setSearchParams omit `state`.
 */

export const AIRLINE_TRANSPORT_ROUTER_STATE_KEY = "gp.airlineTransport.routerState";
/** Legacy key — still read for backward compatibility with in-flight sessions. */
const LEGACY_AIRLINE_SBB_ROUTER_STATE_KEY = "gp.sbb.airlineRouterState";

export type TransportAirlineApiContext =
  | { mode: "hotel" }
  | { mode: "airline"; iataCode: string }
  | { mode: "airline_missing_iata" };

export type AirlineTransportAddresses = {
  departureAddress: string | null;
  departureAddressId: string | null;
  destinationAddress: string | null;
  destinationAddressId: string | null;
};

const EMPTY_AIRLINE_TRANSPORT_ADDRESSES: AirlineTransportAddresses = {
  departureAddress: null,
  departureAddressId: null,
  destinationAddress: null,
  destinationAddressId: null
};

/** Reads departure/destination from airline landing navigation state (location.state or session fallback). */
export function readAirlineTransportAddressesFromNavigationState(locationState?: unknown): AirlineTransportAddresses {
  const state = (locationState ?? readPersistedAirlineRouterState()) as {
    meta?: { source?: string };
    shared?: {
      arrivalDestination?: {
        placeId?: string | null;
        formattedAddress?: { secondary_text?: string | null } | null;
      } | null;
      finalDestination?: {
        placeId?: string | null;
        description?: string | null;
      } | null;
    };
  } | null;

  if (state?.meta?.source !== "airline") {
    return EMPTY_AIRLINE_TRANSPORT_ADDRESSES;
  }

  return {
    departureAddress: state.shared?.arrivalDestination?.formattedAddress?.secondary_text ?? null,
    departureAddressId: state.shared?.arrivalDestination?.placeId ?? null,
    destinationAddress: state.shared?.finalDestination?.description ?? null,
    destinationAddressId: state.shared?.finalDestination?.placeId ?? null
  };
}

function parseAirlineFromLocationState(locationState: unknown): TransportAirlineApiContext | null {
  const state = locationState as {
    meta?: { source?: string };
    shared?: { iataCode?: string | null };
  } | null;
  if (state?.meta?.source !== "airline") {
    return null;
  }
  const raw = state?.shared?.iataCode;
  const iataCode = typeof raw === "string" ? raw.trim() : "";
  if (!iataCode) {
    return { mode: "airline_missing_iata" };
  }
  return { mode: "airline", iataCode };
}

function readRawPersistedRouterState(): string | null {
  try {
    return (
      sessionStorage.getItem(AIRLINE_TRANSPORT_ROUTER_STATE_KEY) ||
      sessionStorage.getItem(LEGACY_AIRLINE_SBB_ROUTER_STATE_KEY)
    );
  } catch {
    return null;
  }
}

export function readPersistedAirlineRouterState(): unknown {
  try {
    const raw = readRawPersistedRouterState();
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Call on airline navigation (e.g. navigateToTransportOption) and from transport pages (useLayoutEffect). */
export function persistAirlineFromNavigationState(locationState: unknown): void {
  const state = locationState as { meta?: { source?: string } } | null;
  if (state?.meta?.source !== "airline") {
    return;
  }
  try {
    const serialized = JSON.stringify(locationState);
    sessionStorage.setItem(AIRLINE_TRANSPORT_ROUTER_STATE_KEY, serialized);
  } catch {
    /* ignore */
  }
}

export function clearPersistedAirlineRouterState(): void {
  try {
    sessionStorage.removeItem(AIRLINE_TRANSPORT_ROUTER_STATE_KEY);
    sessionStorage.removeItem(LEGACY_AIRLINE_SBB_ROUTER_STATE_KEY);
  } catch {
    /* ignore */
  }
}

/** Next navigation `state` when history dropped it — restores full airline payload from session. */
export function buildAirlineNavigationState(locationState: unknown): Record<string, unknown> | undefined {
  if (locationState && typeof locationState === "object") {
    return locationState as Record<string, unknown>;
  }
  const persisted = readPersistedAirlineRouterState();
  if (persisted && typeof persisted === "object") {
    return persisted as Record<string, unknown>;
  }
  return undefined;
}

export function resolveTransportAirlineApiContext(locationState: unknown): TransportAirlineApiContext {
  const fromLocation = parseAirlineFromLocationState(locationState);
  if (fromLocation?.mode === "airline") {
    return fromLocation;
  }
  if (fromLocation?.mode === "airline_missing_iata") {
    return fromLocation;
  }

  const fromPersisted = parseAirlineFromLocationState(readPersistedAirlineRouterState());
  if (fromPersisted?.mode === "airline") {
    return fromPersisted;
  }
  if (fromPersisted?.mode === "airline_missing_iata") {
    return fromPersisted;
  }

  return { mode: "hotel" };
}
